import React from 'react';
import { useTranslation } from 'react-i18next';

const API_LINKS = [
  { key: 'apis', href: 'https://www.benzinga.com/apis/', labelKey: 'Quote.Footer.benzinga-apis' },
  {
    key: 'stock-news',
    href: 'https://www.benzinga.com/apis/stock-news-api/',
    labelKey: 'Quote.Footer.stock-news-api',
  },
  {
    key: 'analyst-ratings',
    href: 'https://www.benzinga.com/apis/analyst-ratings-api/',
    labelKey: 'Quote.Footer.analyst-ratings-api',
  },
  {
    key: 'logo',
    href: 'https://www.benzinga.com/apis/company-logo-api/',
    labelKey: 'Quote.Footer.logo-api',
  },
] as const;

const BROWSE_LINKS: Record<string, { href: string; labelKey: string }> = {
  'analyst-ratings': {
    href: 'https://www.benzinga.com/analyst-stock-ratings',
    labelKey: 'Quote.AnalystRatings.analyst-ratings-and-price-targets',
  },
  earnings: {
    href: 'https://www.benzinga.com/calendars/earnings',
    labelKey: 'Quote.Earnings.earnings-estimates-eps',
  },
  events: {
    href: 'https://www.benzinga.com/calendars/events',
    labelKey: 'Quote.Nav.events',
  },
  dividends: {
    href: 'https://www.benzinga.com/calendars/dividends',
    labelKey: 'Quote.Nav.dividends',
  },
  guidance: {
    href: 'https://www.benzinga.com/calendars/guidance',
    labelKey: 'Quote.Guidance.guidance-and-forecast',
  },
  'insider-trades': {
    href: 'https://www.benzinga.com/sec/insider-trades/',
    labelKey: 'Quote.InsiderTrades.insider-trades',
  },
};

interface QuoteFooterProps {
  activeTab?: string;
  showBrowseLink?: boolean;
  showApiLinks?: boolean;
  showAttribution?: boolean;
}

export const QuoteFooter: React.FC<QuoteFooterProps> = ({
  activeTab,
  showBrowseLink = true,
  showApiLinks = true,
  showAttribution = true,
}) => {
  const { t } = useTranslation(['quote', 'common']);
  const browseLink = activeTab ? BROWSE_LINKS[activeTab] : null;

  return (
    <div className="quote-footer mx-auto max-w-[1400px] w-full mt-2">
      {showBrowseLink && browseLink && (
        <div className="browse-section py-3 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            {t('Buttons.browse', { ns: 'common' })}{' '}
            <a href={browseLink.href} className="text-blue-500 hover:underline">
              {t(browseLink.labelKey)}
            </a>{' '}
            {t('Quote.Helpers.on-all-stocks')}
          </p>
        </div>
      )}

      {showApiLinks && (
        <div className="api-links-section py-3 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            {t('Quote.Footer.data-apis')}:{' '}
            {API_LINKS.map((link, idx) => (
              <React.Fragment key={link.key}>
                <a href={link.href} className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                  {t(link.labelKey)}
                </a>
                {idx < API_LINKS.length - 1 && ' · '}
              </React.Fragment>
            ))}
          </p>
        </div>
      )}

      {showAttribution && (
        <div className="attribution-section py-3 border-t border-gray-200">
          <p className="text-xs text-gray-400">
            {t('Quote.Footer.market-data-provided-by')}{' '}
            <a
              href="https://www.benzinga.com/apis/"
              className="text-blue-400 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('Quote.Footer.benzinga-apis')}
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default QuoteFooter;
